const isNull = value => value === null || value === undefined;
const isNotNull = value => value !== null && value !== undefined;

const basicOrder = (one, two) => {
  if (one === two) return 0;
  if (isNull(one)) return 1;
  if (isNull(two)) return -1;
  return (one > two ? 1 : -1);
};
const reverse = sortingFunction => (one, two) => sortingFunction(two, one);
const mapped = (mappingFunction, sortingFunction) => (one, two) => sortingFunction(mappingFunction(one), mappingFunction(two));
const propertyBasedOrder = (propertyName, sortingFunction) => mapped(
    obj => obj[propertyName],
    sortingFunction ? sortingFunction : basicOrder
);
const cascade = (...sortingFunctions) => (one, two) => {
    for (const sortingFunction of sortingFunctions) {
        const comparisonResult = sortingFunction(one, two);
        if (comparisonResult !== 0) return comparisonResult;
    }
    return 0;
};

const statusOrder = mapped(row => row.status === 'ALARM' ? 1 : (row.status === 'WARNING' ? 2 : 3), basicOrder);
const conditionCountOrder = mapped(row => row.conditions ? row.conditions.filter(ci => ci.status !== 'NORMAL').length : 0, reverse(basicOrder));
const alphabeticalCarOrder = propertyBasedOrder('car');
const alphabeticalCarIdOrder = propertyBasedOrder('car_id');
const alphabeticalBogieOrder = propertyBasedOrder('bogie');
const alphabeticalWheelOrder = propertyBasedOrder('wheel');
const dateOrder = mapped(row => new Date(row.lastDataTimestamp), basicOrder);
const conditionValueOrder = (conditionName, reversed) => mapped(row => {
    // Sort by the value of the condition indicator
    const condition = row.conditions.find(condition => condition.name === conditionName);
    if (isNotNull(condition)) return Number(condition.value);
    // Put missing values last depending on which way things go
    return (reversed ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER);
}, basicOrder);

function getBearingSortingFunction(type, reversed) {
    function getSortingFunctionBy(type) {
        if (type && type.startsWith('ci:')) {
            const conditionName = type.substring(3);
            return conditionValueOrder(conditionName, reversed);
        }
        switch (type) {
            case 'status':
                return cascade(statusOrder, conditionCountOrder);
            case 'car':
                return alphabeticalCarOrder;
            case 'bogie':
                return alphabeticalBogieOrder;
            case 'wheel':
            case 'asset':
                return alphabeticalWheelOrder;
            case 'lastDataTimestamp':
                return dateOrder;
            default:
                return cascade(statusOrder, conditionCountOrder, alphabeticalCarOrder, alphabeticalBogieOrder, alphabeticalBogieOrder);
        }
    }

    const sortFunction = getSortingFunctionBy(type);
    return reversed ? reverse(sortFunction) : sortFunction;
}

const diagnosticStatusOrder = mapped(row => row.diagnosticStatus === 'ALARM' ? 1 : (row.diagnosticStatus === 'WARNING' ? 2 : 3), basicOrder);
const bearingMaintenanceStatusOrder = mapped(row => row.bearingMaintenanceStatus === 'ALARM' ? 1 : (row.bearingMaintenanceStatus === 'WARNING' ? 2 : 3), basicOrder);
const alphabeticalTrainLineNumberOrder = propertyBasedOrder('trainLineNumber');
const alphabeticalInServiceOrder = propertyBasedOrder('inService');
const alphabeticalSpecialCarOrder = propertyBasedOrder('specialCar');

function getCarSortingFunction(type, reversed) {
    function getSortingFunctionBy(type) {
        switch (type) {
            case 'car_id':
                return alphabeticalCarIdOrder;
            case 'trainLineNumber':
                return alphabeticalTrainLineNumberOrder;
            case 'diagnosticStatus':
                return diagnosticStatusOrder;
            case 'bearingMaintenanceStatus':
                return bearingMaintenanceStatusOrder;
            case 'inService':
                return alphabeticalInServiceOrder;
            case 'specialCar':
                return alphabeticalSpecialCarOrder;
            default:
                return alphabeticalCarIdOrder;
        }
    }

    const sortFunction = getSortingFunctionBy(type);
    return reversed ? reverse(sortFunction) : sortFunction;
}

const getUnitSortingFunction = (type, reversed) => {
    const sortFunction = basicOrder;
    return reversed ? reverse(sortFunction) : sortFunction;
};

const getUnitConsistSortingFunction = (type, reversed) => {
    function getSortFunction() {
        switch (type) {
            case 'consist':
                return propertyBasedOrder('consist', basicOrder);
            case 'seen_date':
                return propertyBasedOrder('seenDate', mapped(date => new Date(date), basicOrder));
            default:
                return basicOrder;
        }
    }

    const sortFunction = getSortFunction();
    return reversed ? reverse(sortFunction) : sortFunction;
};

export {getBearingSortingFunction, getCarSortingFunction, getUnitSortingFunction, getUnitConsistSortingFunction};